import React from "react";
import { MessageCircle, Phone, ChevronRight, Clock, Shield, Zap } from "lucide-react";
import { CONTACTS, waLink, telLink, LOGOS } from "../lib/contact";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center pt-24 pb-16 overflow-hidden noise-overlay"
    >
      {/* Layered background */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1578857371787-189d8b9eda84?crop=entropy&cs=srgb&fm=jpg&w=1920&q=70"
          alt=""
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 to-black/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/60"></div>
        <div className="absolute inset-0 speed-lines"></div>
      </div>

      {/* Warning tape top */}
      <div className="absolute top-20 left-0 right-0 h-2 warning-tape opacity-90"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-10 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-7 reveal">
          {/* Live badge */}
          <div
            className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-sm bg-black border border-[#f3bd0e]/60 mb-7"
            data-testid="hero-live-badge"
          >
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 live-dot"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span className="text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase text-[#f3bd0e]">
              Online Agora · Atendimento 24h
            </span>
          </div>

          {/* Brand kicker for SEO */}
          <p
            className="text-[11px] sm:text-xs font-bold tracking-[0.35em] uppercase text-white/55 mb-4"
            data-testid="hero-brand-kicker"
          >
            JR Auto Socorro · Junior Auto Socorro 24h · JR Assistência Auto
          </p>

          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl uppercase leading-[0.95] tracking-tighter text-white"
            data-testid="hero-headline"
          >
            Seu problema na rua,
            <br />
            <span className="text-[#f3bd0e] drop-shadow-[0_0_30px_rgba(243,189,14,0.35)]">
              nossa solução
            </span>{" "}
            <span className="relative inline-block">
              na hora!
              <span className="absolute -bottom-2 left-0 h-1.5 w-full warning-tape opacity-90"></span>
            </span>
          </h1>

          <p
            className="mt-8 text-base sm:text-lg text-white/75 max-w-2xl leading-relaxed"
            data-testid="hero-subheadline"
          >
            Assistência Auto 24 Horas no{" "}
            <span className="text-white font-semibold">Morumbi</span>,{" "}
            <span className="text-white font-semibold">Campo Limpo</span>,{" "}
            <span className="text-white font-semibold">Vila Andrade</span>,{" "}
            <span className="text-white font-semibold">Taboão da Serra</span>,{" "}
            <span className="text-white font-semibold">Vila Olímpia</span>,{" "}
            <span className="text-white font-semibold">Itaim Bibi</span>,{" "}
            <span className="text-white font-semibold">Moema</span>,{" "}
            <span className="text-white font-semibold">Saúde</span> e{" "}
            <span className="text-white font-semibold">Interlagos</span>.
            Carga de bateria, troca de estepe, chaveiro automotivo, borracharia
            móvel, auxílio combustível e serviço Anjo da Guarda — com chegada
            rápida e preço justo.
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              href={waLink(CONTACTS.phones[0].raw)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="bg-[#f3bd0e] text-black font-bold uppercase tracking-wide text-sm px-7 py-4 rounded-md hover:bg-yellow-300 hover:shadow-[0_0_30px_rgba(243,189,14,0.55)] transition flex items-center justify-center gap-2 group"
            >
              <MessageCircle className="w-5 h-5" />
              Chamar no WhatsApp
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
            </a>
            <a
              href={telLink(CONTACTS.phones[0].raw)}
              data-testid="hero-call-cta"
              className="bg-transparent border-2 border-white text-white font-bold uppercase tracking-wide text-sm px-7 py-4 rounded-md hover:bg-white hover:text-black transition flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Ligar Agora
            </a>
          </div>

          {/* trust micro-icons */}
          <div className="mt-10 flex flex-wrap gap-5 sm:gap-8 text-sm">
            <div className="flex items-center gap-2 text-white/80" data-testid="hero-trust-1">
              <Clock className="w-4 h-4 text-[#f3bd0e]" />
              <span>Atende 24/7</span>
            </div>
            <div className="flex items-center gap-2 text-white/80" data-testid="hero-trust-2">
              <Zap className="w-4 h-4 text-[#f3bd0e]" />
              <span>Chegada rápida</span>
            </div>
            <div className="flex items-center gap-2 text-white/80" data-testid="hero-trust-3">
              <Shield className="w-4 h-4 text-[#f3bd0e]" />
              <span>Equipe especializada</span>
            </div>
          </div>
        </div>

        {/* Right: hero visual card */}
        <div className="lg:col-span-5 reveal" style={{ animationDelay: "0.15s" }}>
          <div className="relative">
            {/* Glow */}
            <div className="absolute -inset-6 bg-[#f3bd0e]/20 blur-3xl rounded-full"></div>
            <div className="relative bg-[#0A0A0A] border border-white/10 rounded-lg p-6 overflow-hidden">
              <div className="absolute -top-1 left-0 right-0 h-1.5 warning-tape"></div>
              <img
                src={LOGOS.banner}
                alt="JR Auto Socorro 24h - Serviços"
                className="w-full h-auto rounded-md float-soft"
                data-testid="hero-banner-img"
              />
              <div className="mt-5 grid grid-cols-1 gap-3">
                {CONTACTS.phones.map((p) => (
                  <a
                    key={p.raw}
                    href={waLink(p.raw)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-black border border-[#f3bd0e]/50 rounded-md px-3 py-3 hover:bg-[#f3bd0e] hover:text-black text-white transition group"
                    data-testid={`hero-phone-card-${p.raw}`}
                  >
                    <MessageCircle className="w-4 h-4 text-[#f3bd0e] group-hover:text-black" />
                    <span className="font-display text-sm tracking-tight">
                      {p.label}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
